'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { useRenownAuth } from '@powerhousedao/reactor-browser';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { BuilderDriveLink } from '@/modules/__generated__/graphql/switchboard-generated';
import { type SpinUpPersonaId, useSpinUpDrive } from '@/modules/onboarding/hooks/use-spin-up-drive';
import { TOTAL_STEPS } from '@/modules/onboarding/lib/constants';
import type { PersonaId } from '@/modules/onboarding/lib/personas';
import { stepOneSchema, type StepOneValues } from '@/modules/onboarding/lib/schemas';
import { ChooseRoleStep } from './choose-role-step';
import { DoneStep } from './done-step';
import { OnboardingShell } from './onboarding-shell';
import { SpinUpStep } from './spin-up-step';
type Phase = 'choose-role' | 'spinning-up' | 'done';
interface OnboardingStepsProps {
  onComplete: () => void;
  /** Pre-selects a persona on first render (e.g. operator intent from the hero). */
  defaultPersonaId?: PersonaId;
}
function OnboardingSteps(t0) {
  const $ = _c(24);
  const {
    onComplete,
    defaultPersonaId
  } = t0;
  const auth = useRenownAuth();
  const [phase, setPhase] = useState("choose-role");
  const [createdDrive, setCreatedDrive] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zodResolver(stepOneSchema);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = defaultPersonaId ?? undefined as unknown as PersonaId;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      resolver: t1,
      defaultValues: {
        personaId: t2,
        displayName: ""
      }
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const form = useForm(t3);
  let t4;
  let t5;
  if ($[3] !== auth.displayName || $[4] !== form) {
    t4 = () => {
      if (!auth.displayName) {
        return;
      }
      const current = form.getValues("displayName");
      if (current && current.length > 0) {
        return;
      }
      form.setValue("displayName", auth.displayName, {
        shouldDirty: false,
        shouldTouch: false
      });
    };
    t5 = [auth.displayName, form];
    $[3] = auth.displayName;
    $[4] = form;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const {
    mutate: spinUpMutate,
    isPending: spinUpPending,
    isError: spinUpFailed,
    error: spinUpError
  } = useSpinUpDrive();
  let t6;
  if ($[7] !== auth.ensName || $[8] !== form || $[9] !== onComplete || $[10] !== spinUpMutate) {
    t6 = () => {
      const personaId = form.getValues("personaId") as SpinUpPersonaId;
      const name = form.getValues("displayName");
      setPhase("spinning-up");
      spinUpMutate({
        personaId,
        name,
        ensName: auth.ensName
      }, {
        onSuccess: drive => {
          setCreatedDrive(drive);
          onComplete();
          setPhase("done");
        }
      });
    };
    $[7] = auth.ensName;
    $[8] = form;
    $[9] = onComplete;
    $[10] = spinUpMutate;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const submitSpinUp = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setPhase("choose-role");
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleBackToChooseRole = t7;
  const currentStep = phase === "done" ? TOTAL_STEPS + 1 : phase === "spinning-up" ? TOTAL_STEPS : 2;
  const isCreatingDrive = phase === "spinning-up" && spinUpPending;
  let t8;
  if ($[13] !== createdDrive || $[14] !== form || $[15] !== phase || $[16] !== spinUpError || $[17] !== spinUpFailed || $[18] !== submitSpinUp) {
    t8 = phase === "done" ? <DoneStep drive={createdDrive} /> : phase === "spinning-up" ? <SpinUpStep isError={spinUpFailed} error={spinUpError} onRetry={submitSpinUp} onBack={handleBackToChooseRole} /> : <ChooseRoleStep form={form} onSubmit={submitSpinUp} />;
    $[13] = createdDrive;
    $[14] = form;
    $[15] = phase;
    $[16] = spinUpError;
    $[17] = spinUpFailed;
    $[18] = submitSpinUp;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== currentStep || $[21] !== isCreatingDrive || $[22] !== t8) {
    t9 = <OnboardingShell currentStep={currentStep} loading={isCreatingDrive}>{t8}</OnboardingShell>;
    $[20] = currentStep;
    $[21] = isCreatingDrive;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
export { OnboardingSteps };